/**
 * LicensesPage - Gestão de Licenças de Software
 * P0.3 - License Tracking Complete
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger
} from '@/components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { licensesAPI, clientsAPI } from '@/lib/api';
import { EmptyStatePanel } from '@/components/common/EmptyStatePanel';
import { ModuleStateBoundary } from '@/components/common/ModuleStateBoundary';
import { useFetchTelemetry } from '@/hooks/useFetchTelemetry';
import { safeRequest } from '@/lib/safeRequest';
import WhatsAppButton from '@/components/WhatsAppButton';
import {
  Key,
  Plus,
  Search,
  AlertTriangle,
  Calendar,
  User,
  Clock,
  CheckCircle,
  XCircle,
  MoreVertical,
  Edit,
  Trash2,
  Bell,
  RefreshCw,
  Shield,
  History,
  Monitor,
  FileText,
  Layers,
  Filter,
  Cloud,
  Lock,
  Settings,
  Database,
  Repeat,
  Smartphone,
  Laptop
} from 'lucide-react';
import { cn, formatCurrency } from '@/lib/utils';

// Category icons mapping
const CATEGORY_ICONS = {
  'Antivirus': Shield,
  'Microsoft Office / Microsoft 365': FileText,
  'Windows (licenças / activation)': Monitor,
  'Adobe (Acrobat/CC)': Layers,
  'Backup/Cloud (OneDrive/Google Drive/iCloud)': Cloud,
  'VPN / Segurança': Lock,
  'MDM / Gestão (Intune/Apple Business Manager)': Settings,
  'Password Manager': Key
};

// Category colors
const CATEGORY_COLORS = {
  'Antivirus': 'bg-green-100 text-green-800 border-green-300',
  'Microsoft Office / Microsoft 365': 'bg-blue-100 text-blue-800 border-blue-300',
  'Windows (licenças / activation)': 'bg-cyan-100 text-cyan-800 border-cyan-300',
  'Adobe (Acrobat/CC)': 'bg-red-100 text-red-800 border-red-300',
  'Backup/Cloud (OneDrive/Google Drive/iCloud)': 'bg-purple-100 text-purple-800 border-purple-300',
  'VPN / Segurança': 'bg-orange-100 text-orange-800 border-orange-300',
  'MDM / Gestão (Intune/Apple Business Manager)': 'bg-slate-100 text-slate-800 border-slate-300',
  'Password Manager': 'bg-amber-100 text-amber-800 border-amber-300'
};

const formatDate = (date) => {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('pt-MZ', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

const getDaysUntilExpiry = (endDate) => {
  if (!endDate) return null;
  const now = new Date();
  const expiry = new Date(endDate);
  const diff = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
  return diff;
};

const getExpiryBadge = (days) => {
  if (days === null) return <Badge variant="secondary">Perpétua</Badge>;
  if (days < 0) return <Badge variant="destructive">Expirado há {Math.abs(days)} dias</Badge>;
  if (days === 0) return <Badge variant="destructive">Expira hoje!</Badge>;
  if (days <= 7) return <Badge className="bg-red-100 text-red-800 border-red-300">{days} dias</Badge>;
  if (days <= 15) return <Badge className="bg-orange-100 text-orange-800 border-orange-300">{days} dias</Badge>;
  if (days <= 30) return <Badge className="bg-yellow-100 text-yellow-800 border-yellow-300">{days} dias</Badge>;
  return <Badge className="bg-green-100 text-green-800 border-green-300">{days} dias</Badge>;
};

export default function LicensesPage() {
  const [licenses, setLicenses] = useState([]);
  const [licenseTypes, setLicenseTypes] = useState([]);
  const [clients, setClients] = useState([]);
  const [categories, setCategories] = useState([]);
  const [alertOptions, setAlertOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('licenses');
  
  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLicense, setEditingLicense] = useState(null);
  const [renewDialogOpen, setRenewDialogOpen] = useState(false);
  const [renewingLicense, setRenewingLicense] = useState(null);
  const [renewEndDate, setRenewEndDate] = useState('');
  const [renewNotes, setRenewNotes] = useState('');
  const [renewalHistoryOpen, setRenewalHistoryOpen] = useState(false);
  const [renewalHistory, setRenewalHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const { partialIssues, captureFetchTelemetry, resetFetchTelemetry } = useFetchTelemetry();
  
  const [formData, setFormData] = useState({
    client_id: '',
    license_type_id: '',
    license_key: '',
    seat_count: 1,
    start_date: new Date().toISOString().split('T')[0],
    end_date: '',
    renewable: true,
    linked_device_sn: '',
    linked_device_name: '',
    monitor_expiration: true,
    alert_days_before: 30,
    notes: ''
  });

  // Load data
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      resetFetchTelemetry();
      const [licensesRes, typesRes, clientsRes, categoriesRes, alertsRes] = await Promise.all([
        safeRequest(() => licensesAPI.list(), { data: [] }, { telemetry: { module: 'licenses', endpoint: 'licenses.list', onEvent: captureFetchTelemetry } }),
        safeRequest(() => licensesAPI.getTypes(), { data: [] }, { telemetry: { module: 'licenses', endpoint: 'licenses.types', onEvent: captureFetchTelemetry } }),
        safeRequest(() => clientsAPI.listActive(), { data: [] }, { telemetry: { module: 'licenses', endpoint: 'clients.listActive', onEvent: captureFetchTelemetry } }),
        safeRequest(() => licensesAPI.getCategories(), { data: [] }, { telemetry: { module: 'licenses', endpoint: 'licenses.categories', onEvent: captureFetchTelemetry } }),
        safeRequest(() => licensesAPI.listAlertOptions(), { data: [] }, { telemetry: { module: 'licenses', endpoint: 'licenses.alertOptions', onEvent: captureFetchTelemetry } })
      ]);
      
      setLicenses(licensesRes.data || []);
      setLicenseTypes(typesRes.data || []);
      setClients(clientsRes.data || []);
      setCategories(categoriesRes.data || []);
      setAlertOptions(alertsRes.data || []);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erro ao carregar dados');
      
      // If no license types, try to seed
      if (error.response?.status === 404 || (licenseTypes.length === 0)) {
        try {
          await licensesAPI.seedTypes();
          const typesRes = await licensesAPI.getTypes();
          setLicenseTypes(typesRes.data || []);
          const categoriesRes = await licensesAPI.getCategories();
          setCategories(categoriesRes.data || []);
          toast.success('Tipos de licença criados automaticamente');
        } catch (seedError) {
          console.error('Error seeding:', seedError);
        }
      }
    } finally {
      setLoading(false);
    }
  }, [licenseTypes.length, captureFetchTelemetry, resetFetchTelemetry]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Seed types manually
  const handleSeedTypes = async () => {
    try {
      const res = await licensesAPI.seedTypes();
      toast.success(res.data.message);
      loadData();
    } catch (error) {
      toast.error('Erro ao criar tipos de licença');
    }
  };

  // Filter licenses
  const filteredLicenses = licenses.filter(license => {
    const matchesSearch = !searchTerm || 
      license.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      license.license_type_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      license.license_key?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      license.linked_device_sn?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = categoryFilter === 'all' || license.license_category === categoryFilter;
    
    const days = getDaysUntilExpiry(license.end_date || license.expiration_date);
    let matchesStatus = true;
    if (statusFilter === 'active') matchesStatus = days === null || days > 30;
    if (statusFilter === 'expiring') matchesStatus = days !== null && days >= 0 && days <= 30;
    if (statusFilter === 'expired') matchesStatus = days !== null && days < 0;
    
    return matchesSearch && matchesCategory && matchesStatus;
  });

  // Handle form changes
  const handleTypeChange = (typeId) => {
    const type = licenseTypes.find(t => t.id === typeId);
    if (type) {
      const startDate = new Date(formData.start_date || new Date());
      let endDate = '';
      
      if (type.default_duration_days && type.default_duration_days > 0) {
        const expDate = new Date(startDate);
        expDate.setDate(expDate.getDate() + type.default_duration_days);
        endDate = expDate.toISOString().split('T')[0];
      }
      
      setFormData({
        ...formData,
        license_type_id: typeId,
        end_date: endDate,
        renewable: type.default_duration_days > 0
      });
    }
  };

  // Submit form
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.client_id || !formData.license_type_id) {
      toast.error('Seleccione cliente e tipo de licença');
      return;
    }

    try {
      const payload = {
        ...formData,
        start_date: new Date(formData.start_date).toISOString(),
        end_date: formData.end_date ? new Date(formData.end_date).toISOString() : null,
        seat_count: parseInt(formData.seat_count) || 1,
        alert_days_before: parseInt(formData.alert_days_before) || 30
      };

      if (editingLicense) {
        await licensesAPI.update(editingLicense.id, payload);
        toast.success('Licença actualizada!');
      } else {
        await licensesAPI.create(payload);
        toast.success('Licença registada!');
      }

      setDialogOpen(false);
      setEditingLicense(null);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao guardar licença');
    }
  };

  const resetForm = () => {
    setFormData({
      client_id: '',
      license_type_id: '',
      license_key: '',
      seat_count: 1,
      start_date: new Date().toISOString().split('T')[0],
      end_date: '',
      renewable: true,
      linked_device_sn: '',
      linked_device_name: '',
      monitor_expiration: true,
      alert_days_before: 30,
      notes: ''
    });
  };

  const handleEdit = (license) => {
    setEditingLicense(license);
    setFormData({
      client_id: license.client_id || '',
      license_type_id: license.license_type_id || '',
      license_key: license.license_key || '',
      seat_count: license.seat_count || 1,
      start_date: license.start_date ? new Date(license.start_date).toISOString().split('T')[0] : '',
      end_date: (license.end_date || license.expiration_date) ? new Date(license.end_date || license.expiration_date).toISOString().split('T')[0] : '',
      renewable: license.renewable ?? true,
      linked_device_sn: license.linked_device_sn || '',
      linked_device_name: license.linked_device_name || '',
      monitor_expiration: license.monitor_expiration ?? true,
      alert_days_before: license.alert_days_before || 30,
      notes: license.notes || ''
    });
    setDialogOpen(true);
  };

  const handleDelete = async (license) => {
    if (!window.confirm(`Remover licença "${license.license_type_name}" de ${license.client_name}?`)) return;
    
    try {
      await licensesAPI.delete(license.id);
      toast.success('Licença removida');
      loadData();
    } catch (error) {
      toast.error('Erro ao remover licença');
    }
  };

  const handleRenew = async () => {
    if (!renewEndDate) {
      toast.error('Seleccione a nova data de expiração');
      return;
    }
    
    try {
      await licensesAPI.renew(renewingLicense.id, new Date(renewEndDate).toISOString(), renewNotes);
      toast.success('Licença renovada com sucesso!');
      setRenewDialogOpen(false);
      setRenewingLicense(null);
      setRenewEndDate('');
      setRenewNotes('');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao renovar licença');
    }
  };

  const openRenewDialog = (license) => {
    setRenewingLicense(license);
    // Default: extend by same duration
    const currentEnd = new Date(license.end_date || license.expiration_date);
    const start = new Date(license.start_date || license.activation_date);
    const duration = currentEnd - start;
    const newEnd = new Date(currentEnd.getTime() + duration);
    setRenewEndDate(newEnd.toISOString().split('T')[0]);
    setRenewDialogOpen(true);
  };

  const openRenewalHistory = async (license) => {
    try {
      setHistoryLoading(true);
      setRenewingLicense(license);
      const response = await licensesAPI.getRenewals(license.id);
      setRenewalHistory(response.data?.renewals || []);
      setRenewalHistoryOpen(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao carregar histórico de renovações');
    } finally {
      setHistoryLoading(false);
    }
  };

  // Stats
  const stats = {
    total: licenses.length,
    active: licenses.filter(l => {
      const days = getDaysUntilExpiry(l.end_date || l.expiration_date);
      return days === null || days > 30;
    }).length,
    expiringSoon: licenses.filter(l => {
      const days = getDaysUntilExpiry(l.end_date || l.expiration_date);
      return days !== null && days >= 0 && days <= 30;
    }).length,
    expired: licenses.filter(l => {
      const days = getDaysUntilExpiry(l.end_date || l.expiration_date);
      return days !== null && days < 0;
    }).length
  };

  const hasLicenseData = Boolean(licenses.length || licenseTypes.length || clients.length || categories.length || alertOptions.length);

  return (
    <ModuleStateBoundary
      loading={loading}
      hasData={hasLicenseData}
      partialIssues={partialIssues}
      loadingTestId="licenses-page-loading"
      partialIssuesTestId="licenses-page-partial-issues"
      emptyState={
        <div className="space-y-6 p-6" data-testid="licenses-page">
          <EmptyStatePanel
            title="Sem dados disponíveis"
            description="Nenhum registo encontrado para Licenças. Pode configurar ou importar dados para começar."
            testId="licenses-page-empty-state"
          />
        </div>
      }
    >
    <div className="space-y-6 p-6" data-testid="licenses-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2" style={{fontFamily: 'Manrope'}}>
            <Key className="h-6 w-6 text-primary" />
            Gestão de Licenças
          </h1>
          <p className="text-muted-foreground">Rastreamento de licenças de software vendidas aos clientes</p>
        </div>
        <div className="flex gap-2">
          {licenseTypes.length === 0 && (
            <Button variant="outline" onClick={handleSeedTypes} data-testid="seed-types-btn">
              <Database className="h-4 w-4 mr-2" />
              Criar Tipos
            </Button>
          )}
          <Button onClick={() => { resetForm(); setEditingLicense(null); setDialogOpen(true); }} data-testid="new-license-btn">
            <Plus className="h-4 w-4 mr-2" />
            Nova Licença
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
              <Key className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Activas</p>
                <p className="text-2xl font-bold text-green-600">{stats.active}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>
        <Card className={stats.expiringSoon > 0 ? "border-orange-300 bg-orange-50" : ""}>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">A Expirar (30 dias)</p>
                <p className="text-2xl font-bold text-orange-600">{stats.expiringSoon}</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-orange-600" />
            </div>
          </CardContent>
        </Card>
        <Card className={stats.expired > 0 ? "border-red-300 bg-red-50" : ""}>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Expiradas</p>
                <p className="text-2xl font-bold text-red-600">{stats.expired}</p>
              </div>
              <XCircle className="h-8 w-8 text-red-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="relative flex-1 min-w-[200px] max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Pesquisar cliente, licença, chave, S/N..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
                data-testid="search-input"
              />
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-56" data-testid="category-filter">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Categoria" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas Categorias</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40" data-testid="status-filter">
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="active">Activas</SelectItem>
                <SelectItem value="expiring">A Expirar</SelectItem>
                <SelectItem value="expired">Expiradas</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="icon" onClick={loadData} data-testid="refresh-btn">
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Licenses Table */}
      <Card>
        <CardHeader>
          <CardTitle>Licenças Vendidas ({filteredLicenses.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {filteredLicenses.length === 0 ? (
            <div className="text-center py-12">
              <Key className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">Nenhuma licença encontrada</p>
              <Button variant="outline" className="mt-4" onClick={() => setDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Registar Primeira Licença
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Licença</TableHead>
                  <TableHead>Seats</TableHead>
                  <TableHead>Chave / Dispositivo</TableHead>
                  <TableHead>Início</TableHead>
                  <TableHead>Expiração</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead className="text-right">Acções</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredLicenses.map(license => {
                  const days = getDaysUntilExpiry(license.end_date || license.expiration_date);
                  const CategoryIcon = CATEGORY_ICONS[license.license_category] || Key;
                  
                  return (
                    <TableRow 
                      key={license.id} 
                      className={cn(days !== null && days < 0 && "bg-red-50")}
                      data-testid={`license-row-${license.id}`}
                    >
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-muted-foreground" />
                          <div>
                            <p className="font-medium">{license.client_name || 'N/A'}</p>
                            <p className="text-xs text-muted-foreground">{license.client_phone || '-'}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          <Badge variant="outline" className={cn("text-xs w-fit", CATEGORY_COLORS[license.license_category])}>
                            <CategoryIcon className="h-3 w-3 mr-1" />
                            {license.license_category?.split(' ')[0]}
                          </Badge>
                          <span className="font-medium text-sm">{license.license_type_name}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{license.seat_count || 1}</Badge>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          {license.license_key && (
                            <code className="text-xs bg-slate-100 px-2 py-1 rounded block">
                              {license.license_key}
                            </code>
                          )}
                          {license.linked_device_sn && (
                            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                              <Laptop className="h-3 w-3" />
                              {license.linked_device_name || license.linked_device_sn}
                            </div>
                          )}
                          {!license.license_key && !license.linked_device_sn && '-'}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-sm">
                          <Calendar className="h-3 w-3 text-muted-foreground" />
                          {formatDate(license.start_date || license.activation_date)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-sm">
                          <Clock className="h-3 w-3 text-muted-foreground" />
                          {formatDate(license.end_date || license.expiration_date)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          {getExpiryBadge(days)}
                          {license.renewable && (
                            <Repeat className="h-3 w-3 text-blue-500" title="Renovável" />
                          )}
                          {license.monitor_expiration && (
                            <Bell className="h-3 w-3 text-blue-500" title="Alertas activos" />
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" data-testid={`license-actions-${license.id}`}>
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleEdit(license)}>
                              <Edit className="h-4 w-4 mr-2" />
                              Editar
                            </DropdownMenuItem>
                            {license.renewable && (days === null || days <= 30) && (
                              <DropdownMenuItem onClick={() => openRenewDialog(license)}>
                                <Repeat className="h-4 w-4 mr-2" />
                                Renovar
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={() => openRenewalHistory(license)}>
                              <History className="h-4 w-4 mr-2" />
                              Histórico de Renovações
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            {license.client_phone && (
                              <DropdownMenuItem asChild>
                                <WhatsAppButton
                                  phone={license.client_phone}
                                  clientName={license.client_name}
                                  templateType="license_expiring"
                                  variables={{
                                    license_name: license.license_type_name,
                                    expiration_date: formatDate(license.end_date || license.expiration_date),
                                    days_remaining: String(days || 0)
                                  }}
                                  variant="ghost"
                                  size="sm"
                                  className="w-full justify-start"
                                />
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              onClick={() => handleDelete(license)}
                              className="text-red-600"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Remover
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="h-5 w-5 text-primary" />
              {editingLicense ? 'Editar Licença' : 'Registar Nova Licença'}
            </DialogTitle>
            <DialogDescription>
              {editingLicense ? 'Actualize os dados da licença' : 'Registe uma licença vendida a um cliente'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Client */}
            <div className="space-y-2">
              <Label htmlFor="client">Cliente *</Label>
              <Select 
                value={formData.client_id} 
                onValueChange={(v) => setFormData({...formData, client_id: v})}
              >
                <SelectTrigger data-testid="select-client">
                  <SelectValue placeholder="Seleccionar cliente" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map(client => (
                    <SelectItem key={client.id} value={client.id}>
                      {client.name} {client.nuit ? `(${client.nuit})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* License Type */}
            <div className="space-y-2">
              <Label htmlFor="type">Tipo de Licença *</Label>
              <Select 
                value={formData.license_type_id} 
                onValueChange={handleTypeChange}
              >
                <SelectTrigger data-testid="select-license-type">
                  <SelectValue placeholder="Seleccionar licença" />
                </SelectTrigger>
                <SelectContent className="max-h-80">
                  {categories.map(cat => (
                    <React.Fragment key={cat}>
                      <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground bg-slate-50 sticky top-0">
                        {cat}
                      </div>
                      {licenseTypes.filter(t => t.category === cat).map(type => (
                        <SelectItem key={type.id} value={type.id}>
                          <div className="flex items-center gap-2">
                            {type.name}
                            <div className="flex gap-1">
                              {type.supports_windows && <Monitor className="h-3 w-3 text-blue-500" title="Windows" />}
                              {type.supports_macos && <Smartphone className="h-3 w-3 text-slate-500" title="macOS/iOS" />}
                            </div>
                            {type.default_duration_days > 0 && (
                              <span className="text-xs text-muted-foreground">({type.default_duration_days} dias)</span>
                            )}
                          </div>
                        </SelectItem>
                      ))}
                    </React.Fragment>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Seats & License Key */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="seats">Nº de Licenças (Seats)</Label>
                <Input
                  id="seats"
                  type="number"
                  min="1"
                  value={formData.seat_count}
                  onChange={(e) => setFormData({...formData, seat_count: parseInt(e.target.value) || 1})}
                  data-testid="input-seats"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="key">Chave da Licença</Label>
                <Input
                  id="key"
                  value={formData.license_key}
                  onChange={(e) => setFormData({...formData, license_key: e.target.value})}
                  placeholder="XXXXX-XXXXX-XXXXX"
                  className="font-mono"
                  data-testid="input-key"
                />
              </div>
            </div>

            {/* Dates */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="start">Data de Início *</Label>
                <Input
                  id="start"
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                  required
                  data-testid="input-start-date"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="end">Data de Expiração</Label>
                <Input
                  id="end"
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                  data-testid="input-end-date"
                />
              </div>
            </div>

            {/* Device Linking */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="device_sn">Dispositivo (SN/IMEI)</Label>
                <Input
                  id="device_sn"
                  value={formData.linked_device_sn}
                  onChange={(e) => setFormData({...formData, linked_device_sn: e.target.value})}
                  placeholder="Número de série/IMEI"
                  data-testid="input-device-sn"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="device_name">Nome do Dispositivo</Label>
                <Input
                  id="device_name"
                  value={formData.linked_device_name}
                  onChange={(e) => setFormData({...formData, linked_device_name: e.target.value})}
                  placeholder="Ex: MacBook Pro do João"
                  data-testid="input-device-name"
                />
              </div>
            </div>

            {/* Options Row */}
            <div className="flex flex-wrap gap-6 p-3 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="renewable"
                  checked={formData.renewable}
                  onCheckedChange={(c) => setFormData({...formData, renewable: c})}
                />
                <Label htmlFor="renewable" className="cursor-pointer flex items-center gap-1">
                  <Repeat className="h-3 w-3" /> Renovável
                </Label>
              </div>
              
              <div className="flex items-center gap-2">
                <Checkbox
                  id="monitor"
                  checked={formData.monitor_expiration}
                  onCheckedChange={(c) => setFormData({...formData, monitor_expiration: c})}
                />
                <Label htmlFor="monitor" className="cursor-pointer flex items-center gap-1">
                  <Bell className="h-3 w-3" /> Monitorar expiração
                </Label>
              </div>
              
              {formData.monitor_expiration && (
                <div className="flex items-center gap-2">
                  <Label className="text-sm text-muted-foreground">Alertar:</Label>
                  <Select 
                    value={String(formData.alert_days_before)} 
                    onValueChange={(v) => setFormData({...formData, alert_days_before: parseInt(v)})}
                  >
                    <SelectTrigger className="w-36 h-8">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {alertOptions.length > 0 ? alertOptions.map(opt => (
                        <SelectItem key={opt.days} value={String(opt.days)}>{opt.label}</SelectItem>
                      )) : (
                        <>
                          <SelectItem value="7">7 dias antes</SelectItem>
                          <SelectItem value="15">15 dias antes</SelectItem>
                          <SelectItem value="30">30 dias antes</SelectItem>
                          <SelectItem value="60">60 dias antes</SelectItem>
                        </>
                      )}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="notes">Notas</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                placeholder="Observações adicionais..."
                rows={2}
                data-testid="input-notes"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" data-testid="submit-license">
                {editingLicense ? 'Actualizar' : 'Registar Licença'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={renewalHistoryOpen} onOpenChange={setRenewalHistoryOpen}>
        <DialogContent className="max-w-2xl" data-testid="license-renewal-history-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5 text-blue-600" />
              Histórico de Renovações
            </DialogTitle>
            <DialogDescription>
              {renewingLicense ? `${renewingLicense.license_type_name} • ${renewingLicense.client_name}` : 'Licença seleccionada'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3" data-testid="license-renewal-history-list">
            {historyLoading ? (
              <div className="flex items-center justify-center py-8 text-muted-foreground">
                <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                A carregar histórico...
              </div>
            ) : renewalHistory.length === 0 ? (
              <div className="rounded-xl border border-dashed p-6 text-center text-sm text-muted-foreground">
                Nenhuma renovação registada para esta licença.
              </div>
            ) : (
              renewalHistory.map((entry) => (
                <div
                  key={entry.id}
                  className="rounded-xl border border-slate-200 bg-slate-50 p-4"
                  data-testid={`license-renewal-history-${entry.id}`}
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <Badge variant="outline" className="border-blue-200 bg-blue-50 text-blue-700">
                      {formatDate(entry.renewed_at)}
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                      por {entry.renewed_by_name || 'Sistema'}
                    </span>
                  </div>
                  <div className="mt-3 grid gap-3 text-sm md:grid-cols-2">
                    <div>
                      <p className="text-xs uppercase tracking-wide text-muted-foreground">Data anterior</p>
                      <p className="font-medium">{formatDate(entry.old_end_date)}</p>
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wide text-muted-foreground">Nova data</p>
                      <p className="font-medium text-green-700">{formatDate(entry.new_end_date)}</p>
                    </div>
                  </div>
                  {entry.notes && (
                    <div className="mt-3 rounded-lg bg-white p-3 text-sm text-slate-700">
                      {entry.notes}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setRenewalHistoryOpen(false)} data-testid="close-license-renewal-history">
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Renew Dialog */}
      <Dialog open={renewDialogOpen} onOpenChange={setRenewDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Repeat className="h-5 w-5 text-green-600" />
              Renovar Licença
            </DialogTitle>
            <DialogDescription>
              {renewingLicense?.license_type_name} - {renewingLicense?.client_name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="p-3 bg-slate-50 rounded-lg text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Expiração actual:</span>
                <span className="font-medium">{formatDate(renewingLicense?.end_date || renewingLicense?.expiration_date)}</span>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="new_end">Nova Data de Expiração *</Label>
              <Input
                id="new_end"
                type="date"
                value={renewEndDate}
                onChange={(e) => setRenewEndDate(e.target.value)}
                required
                data-testid="renew-end-date"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="renew_notes">Notas da Renovação</Label>
              <Textarea
                id="renew_notes"
                value={renewNotes}
                onChange={(e) => setRenewNotes(e.target.value)}
                placeholder="Ex: Renovação anual, cliente pagou em..."
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setRenewDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleRenew} className="bg-green-600 hover:bg-green-700" data-testid="confirm-renew">
              Renovar Licença
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </ModuleStateBoundary>
  );
}
